import { useCallback, useEffect, useRef, useState } from "react";
import { track } from "./analytics.js";
import { novelLinkSegment } from "./novelLinks.js";
import { buildPath } from "./route.js";

/** Where a share started: the novel's page or the player's ending panel. */
export type ShareSurface = "detail" | "ending";
/** How the link went out: the system share sheet, or copied to the clipboard. */
export type ShareMethod = "native" | "copy";

export interface ShareableNovel {
  id: string;
  title: string;
  description?: string;
  /** Permanent link code; a novel without one is shared by its page's address. */
  short_code?: string;
}

/** How long the "Link copied" confirmation stays up. */
export const SHARE_COPIED_MS = 2000;

/**
 * The link a reader shares: the short `/n/<code>` when the novel has a code
 * (the site answers it with a 301 to the full page, query included), else the
 * novel page's own address (`/novel/<slug>-<code>` or `/novel/<id>`). Either
 * way it carries `?utm_source=share&utm_medium=<surface>` — the same surface
 * `share_clicked` reports — so a visit from a shared link is attributed to
 * sharing instead of counting as "direct".
 */
export function novelShareUrl(
  novel: Pick<ShareableNovel, "id" | "short_code">,
  surface: ShareSurface,
  origin: string = window.location.origin
): string {
  const path = novel.short_code
    ? `/n/${encodeURIComponent(novel.short_code)}`
    : buildPath("detail", novelLinkSegment(novel.id));
  const tags = new URLSearchParams({ utm_source: "share", utm_medium: surface });
  return `${origin}${path}?${tags.toString()}`;
}

/**
 * Shares a novel: the native share sheet when the browser has one (phones, an
 * installed app), otherwise the link is copied. Resolves to how it went out —
 * `"native"` once the sheet opened (shared or dismissed: nothing is copied
 * behind the reader's back), `"copy"` once the link is on the clipboard — or
 * null when neither worked (no clipboard, or it was blocked).
 */
export async function shareNovelLink(
  novel: ShareableNovel,
  surface: ShareSurface,
  nav: Navigator = navigator,
  origin?: string
): Promise<ShareMethod | null> {
  const url = novelShareUrl(novel, surface, origin);
  if (typeof nav.share === "function") {
    try {
      await nav.share({ title: novel.title, text: novel.description, url });
      return "native";
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return "native";
      // Any other failure (not allowed here, unsupported data): copy instead.
    }
  }
  if (!nav.clipboard) return null;
  try {
    await nav.clipboard.writeText(url);
    return "copy";
  } catch {
    return null;
  }
}

/**
 * The Share button's behaviour for one surface: `share(novel)` shares it and
 * reports `share_clicked`; `copied` stays true for a moment after a copy, for
 * the "Link copied" confirmation.
 */
export function useNovelShare(surface: ShareSurface): {
  share: (novel: ShareableNovel) => Promise<void>;
  copied: boolean;
} {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const share = useCallback(
    async (novel: ShareableNovel) => {
      const method = await shareNovelLink(novel, surface);
      if (!method) return;
      track("share_clicked", { novel_id: novel.id, surface, method });
      if (method !== "copy") return;
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), SHARE_COPIED_MS);
    },
    [surface]
  );

  return { share, copied };
}
